import { useState } from 'react'
import { fmtDateTime } from '../../utils/format'
import {
  PlusIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClockIcon,
  ChartBarIcon,
} from '@heroicons/react/24/outline'

interface TemperatureRecord {
  id: string
  equipmentId: string
  equipmentName: string
  location: string
  temperature: number
  expectedMin: number
  expectedMax: number
  recordedAt: string
  recordedBy: string
  status: 'normal' | 'warning' | 'critical'
  notes?: string
  correctionActions?: string
}

interface Equipment {
  id: string
  name: string
  type: 'refrigerator' | 'freezer' | 'oven' | 'display_case' | 'room'
  location: string
  expectedMin: number
  expectedMax: number
  isActive: boolean
  lastReading?: number
  lastReadingAt?: string
}

const mockEquipment: Equipment[] = [
  {
    id: '1',
    name: 'Cámara Fría 1',
    type: 'refrigerator',
    location: 'Almacén Principal',
    expectedMin: 2,
    expectedMax: 6,
    isActive: true,
    lastReading: 4.2,
    lastReadingAt: '2024-01-22T08:00:00Z'
  },
  {
    id: '2',
    name: 'Congelador Ingredientes',
    type: 'freezer',
    location: 'Almacén Principal',
    expectedMin: -20,
    expectedMax: -15,
    isActive: true,
    lastReading: -18.5,
    lastReadingAt: '2024-01-22T08:15:00Z'
  },
  {
    id: '3',
    name: 'Horno Principal',
    type: 'oven',
    location: 'Área de Producción',
    expectedMin: 160,
    expectedMax: 220,
    isActive: true,
    lastReading: 180,
    lastReadingAt: '2024-01-22T07:45:00Z'
  },
  {
    id: '4',
    name: 'Vitrina Exposición',
    type: 'display_case',
    location: 'Punto de Venta',
    expectedMin: 15,
    expectedMax: 25,
    isActive: true,
    lastReading: 22.1,
    lastReadingAt: '2024-01-22T08:30:00Z'
  },
  {
    id: '5',
    name: 'Sala de Producción',
    type: 'room',
    location: 'Área de Producción',
    expectedMin: 18,
    expectedMax: 22,
    isActive: true,
    lastReading: 20.5,
    lastReadingAt: '2024-01-22T08:00:00Z'
  }
]

const mockTemperatureRecords: TemperatureRecord[] = [
  {
    id: '1',
    equipmentId: '1',
    equipmentName: 'Cámara Fría 1',
    location: 'Almacén Principal',
    temperature: 4.2,
    expectedMin: 2,
    expectedMax: 6,
    recordedAt: '2024-01-22T08:00:00Z',
    recordedBy: 'Ana López',
    status: 'normal'
  },
  {
    id: '2',
    equipmentId: '2',
    equipmentName: 'Congelador Ingredientes',
    location: 'Almacén Principal',
    temperature: -18.5,
    expectedMin: -20,
    expectedMax: -15,
    recordedAt: '2024-01-22T08:15:00Z',
    recordedBy: 'Ana López',
    status: 'normal'
  },
  {
    id: '3',
    equipmentId: '1',
    equipmentName: 'Cámara Fría 1',
    location: 'Almacén Principal',
    temperature: 7.2,
    expectedMin: 2,
    expectedMax: 6,
    recordedAt: '2024-01-21T14:30:00Z',
    recordedBy: 'Carlos Ruiz',
    status: 'warning',
    notes: 'Temperatura ligeramente elevada después de carga',
    correctionActions: 'Ajustado termostato y verificado sellado de puerta'
  },
  {
    id: '4',
    equipmentId: '3',
    equipmentName: 'Horno Principal',
    location: 'Área de Producción',
    temperature: 180,
    expectedMin: 160,
    expectedMax: 220,
    recordedAt: '2024-01-22T07:45:00Z',
    recordedBy: 'María González',
    status: 'normal'
  },
  {
    id: '5',
    equipmentId: '4',
    equipmentName: 'Vitrina Exposición',
    location: 'Punto de Venta',
    temperature: 28.5,
    expectedMin: 15,
    expectedMax: 25,
    recordedAt: '2024-01-21T12:00:00Z',
    recordedBy: 'José López',
    status: 'critical',
    notes: 'Temperatura excesiva durante pico de calor',
    correctionActions: 'Activado sistema de enfriamiento adicional, productos reubicados'
  }
]

export default function Temperatures() {
  const [temperatureRecords, setTemperatureRecords] = useState<TemperatureRecord[]>(mockTemperatureRecords)
  const [equipment] = useState<Equipment[]>(mockEquipment)
  const [selectedEquipment, setSelectedEquipment] = useState<string>('all')
  const [selectedStatus, setSelectedStatus] = useState<string>('all')
  const [showAddModal, setShowAddModal] = useState(false)
  const [newRecord, setNewRecord] = useState<Partial<TemperatureRecord>>({})

  const filteredRecords = temperatureRecords.filter(record => {
    const matchesEquipment = selectedEquipment === 'all' || record.equipmentId === selectedEquipment
    const matchesStatus = selectedStatus === 'all' || record.status === selectedStatus
    return matchesEquipment && matchesStatus
  })

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'normal':
        return 'bg-green-100 text-green-800'
      case 'warning':
        return 'bg-yellow-100 text-yellow-800'
      case 'critical':
        return 'bg-red-100 text-red-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const getStatusText = (status: string) => {
    switch (status) {
      case 'normal':
        return 'Normal'
      case 'warning':
        return 'Advertencia'
      case 'critical':
        return 'Crítico'
      default:
        return status
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'normal':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />
      case 'warning':
        return <ExclamationTriangleIcon className="h-5 w-5 text-yellow-500" />
      case 'critical':
        return <ExclamationTriangleIcon className="h-5 w-5 text-red-500" />
      default:
        return <ClockIcon className="h-5 w-5 text-gray-500" />
    }
  }

  const getEquipmentTypeIcon = (type: string) => {
    switch (type) {
      case 'refrigerator':
        return '❄️'
      case 'freezer':
        return '🧊'
      case 'oven':
        return '🔥'
      case 'display_case':
        return '🏪'
      case 'room':
        return '🏠'
      default:
        return '📊'
    }
  }

  const getEquipmentStatus = (equipment: Equipment) => {
    if (!equipment.lastReading) return 'unknown'

    if (equipment.lastReading < equipment.expectedMin || equipment.lastReading > equipment.expectedMax) {
      return equipment.lastReading < equipment.expectedMin - 2 || equipment.lastReading > equipment.expectedMax + 2
        ? 'critical'
        : 'warning'
    }
    return 'normal'
  }

  const handleAddRecord = () => {
    const selectedEq = equipment.find(eq => eq.id === newRecord.equipmentId)
    if (!selectedEq || !newRecord.temperature || !newRecord.recordedBy) return

    let status: 'normal' | 'warning' | 'critical' = 'normal'
    if (newRecord.temperature < selectedEq.expectedMin || newRecord.temperature > selectedEq.expectedMax) {
      status = newRecord.temperature < selectedEq.expectedMin - 2 || newRecord.temperature > selectedEq.expectedMax + 2
        ? 'critical'
        : 'warning'
    }

    const record: TemperatureRecord = {
      id: Math.random().toString(36).substr(2, 9),
      equipmentId: selectedEq.id,
      equipmentName: selectedEq.name,
      location: selectedEq.location,
      temperature: newRecord.temperature,
      expectedMin: selectedEq.expectedMin,
      expectedMax: selectedEq.expectedMax,
      recordedAt: new Date().toISOString(),
      recordedBy: newRecord.recordedBy,
      status,
      notes: newRecord.notes,
      correctionActions: newRecord.correctionActions
    }

    setTemperatureRecords([record, ...temperatureRecords])
    setShowAddModal(false)
    setNewRecord({})
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-semibold leading-6 text-gray-900">
            Control de Temperaturas
          </h1>
          <p className="mt-2 text-sm text-gray-700">
            Monitoreo y registro de temperaturas de equipos críticos según APPCC.
          </p>
        </div>
        <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none">
          <button
            onClick={() => setShowAddModal(true)}
            className="inline-flex items-center justify-center rounded-md border border-transparent bg-primary-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 sm:w-auto"
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            Nuevo Registro
          </button>
        </div>
      </div>

      {/* Statistics */}
      <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-4">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CheckCircleIcon className="h-6 w-6 text-green-500" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Equipos Normales
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {equipment.filter(eq => getEquipmentStatus(eq) === 'normal').length}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ExclamationTriangleIcon className="h-6 w-6 text-yellow-500" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Advertencias
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {equipment.filter(eq => getEquipmentStatus(eq) === 'warning').length}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ExclamationTriangleIcon className="h-6 w-6 text-red-500" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Críticos
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {equipment.filter(eq => getEquipmentStatus(eq) === 'critical').length}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ChartBarIcon className="h-6 w-6 text-blue-500" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Registros Hoy
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {temperatureRecords.filter(record =>
                      new Date(record.recordedAt).toDateString() === new Date().toDateString()
                    ).length}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Equipment Status Overview */}
      <div className="mt-8">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Estado Actual de Equipos</h3>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {equipment.map((eq) => (
            <div key={eq.id} className="bg-white overflow-hidden shadow rounded-lg border">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="text-2xl">{getEquipmentTypeIcon(eq.type)}</div>
                  </div>
                  <div className="ml-4 flex-1">
                    <div className="flex items-center justify-between">
                      <h4 className="text-sm font-medium text-gray-900">{eq.name}</h4>
                      {getStatusIcon(getEquipmentStatus(eq))}
                    </div>
                    <div className="text-sm text-gray-500">{eq.location}</div>
                    <div className="mt-2">
                      <div className="text-lg font-semibold text-gray-900">
                        {eq.lastReading}°C
                      </div>
                      <div className="text-xs text-gray-500">
                        Rango: {eq.expectedMin}°C - {eq.expectedMax}°C
                      </div>
                      {eq.lastReadingAt && (
                        <div className="text-xs text-gray-500">
                          Última lectura: {fmtDateTime(eq.lastReadingAt)}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Filters */}
      <div className="mt-8 flex flex-col sm:flex-row gap-4">
        <div>
          <select
            value={selectedEquipment}
            onChange={(e) => setSelectedEquipment(e.target.value)}
            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
          >
            <option value="all">Todos los equipos</option>
            {equipment.map((eq) => (
              <option key={eq.id} value={eq.id}>
                {eq.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <select
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
          >
            <option value="all">Todos los estados</option>
            <option value="normal">Normal</option>
            <option value="warning">Advertencia</option>
            <option value="critical">Crítico</option>
          </select>
        </div>
      </div>

      {/* Temperature Records Table */}
      <div className="mt-8 flow-root">
        <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
            <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Fecha/Hora
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Equipo
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Temperatura
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Rango Esperado
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Estado
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Registrado por
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Observaciones
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredRecords.map((record) => (
                    <tr key={record.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {fmtDateTime(record.recordedAt)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {record.equipmentName}
                        </div>
                        <div className="text-sm text-gray-500">
                          {record.location}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          🌡️
                          <span className={`text-sm font-medium ${
                            record.status === 'critical' ? 'text-red-600' :
                            record.status === 'warning' ? 'text-yellow-600' :
                            'text-gray-900'
                          }`}>
                            {record.temperature}°C
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {record.expectedMin}°C - {record.expectedMax}°C
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          {getStatusIcon(record.status)}
                          <span className={`ml-2 inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(record.status)}`}>
                            {getStatusText(record.status)}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {record.recordedBy}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {record.notes && (
                          <div className="mb-1">
                            <span className="font-medium">Notas:</span> {record.notes}
                          </div>
                        )}
                        {record.correctionActions && (
                          <div>
                            <span className="font-medium">Acciones:</span> {record.correctionActions}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Add Record Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-2xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Nuevo Registro de Temperatura
                </h3>
                <button
                  onClick={() => setShowAddModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <span className="sr-only">Cerrar</span>
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Equipo
                  </label>
                  <select
                    value={newRecord.equipmentId || ''}
                    onChange={(e) => setNewRecord({ ...newRecord, equipmentId: e.target.value })}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  >
                    <option value="">Seleccionar equipo...</option>
                    {equipment.map((eq) => (
                      <option key={eq.id} value={eq.id}>
                        {eq.name} ({eq.location})
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Temperatura (°C)
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    value={newRecord.temperature || ''}
                    onChange={(e) => setNewRecord({ ...newRecord, temperature: Number(e.target.value) })}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Registrado por
                  </label>
                  <input
                    type="text"
                    value={newRecord.recordedBy || ''}
                    onChange={(e) => setNewRecord({ ...newRecord, recordedBy: e.target.value })}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    placeholder="Nombre del operador"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Observaciones
                  </label>
                  <textarea
                    rows={3}
                    value={newRecord.notes || ''}
                    onChange={(e) => setNewRecord({ ...newRecord, notes: e.target.value })}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    placeholder="Observaciones sobre la medición..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Acciones Correctivas (si aplica)
                  </label>
                  <textarea
                    rows={3}
                    value={newRecord.correctionActions || ''}
                    onChange={(e) => setNewRecord({ ...newRecord, correctionActions: e.target.value })}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    placeholder="Acciones tomadas si la temperatura está fuera del rango..."
                  />
                </div>
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={() => setShowAddModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleAddRecord}
                  disabled={!newRecord.equipmentId || !newRecord.temperature || !newRecord.recordedBy}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  Guardar Registro
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}